import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import styled, { css } from "styled-components";

import { controlFocusRing, controlTransition } from "../foundations/controlStyles";
import { TruncatedText } from "../TruncatedText";
import { MarkerRail, MarkerRailTrack } from "./TimelineMarkers";
import { TickRegion, TimelineTrack } from "./TimelineTrack";
import type { MediaTimelineTrackVariant, TimelineDragTarget } from "./timelineTypes";

/* The playhead handle lives in the ruler while its line lives in the bordered track rows, and
   both resolve the same percentage position against their own containing block — the ruler needs
   the rows' 1px inline border (transparent: no visual edge) so the two stay aligned at every
   viewport position instead of drifting up to ±1px toward the edges. */
export const TimelineRuler = styled(TickRegion)<{ $disabled: boolean; $focusVisible: boolean }>`
  border-inline: 1px solid transparent;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  touch-action: ${({ $disabled }) => ($disabled ? "auto" : "none")};
  ${({ $focusVisible }) => $focusVisible && controlFocusRing("2px")}
`;

export const TimelineTrackStack = styled.div`
  position: relative;
  display: grid;
  min-width: 0;
  grid-template-columns: minmax(0, 1fr);
`;

/** Labels sit outside the measured time lane; their height follows the actual row. */
export const TimelineTrackRowFrame = styled.div<{
  $gapBefore: boolean;
  $joinBefore: boolean;
  $joinAfter: boolean;
  $toolsOpen?: boolean;
}>`
  position: relative;
  min-width: 0;
  margin-top: ${({ $gapBefore }) => ($gapBefore ? "0.35rem" : "0")};

  ${({ $joinBefore, theme }) =>
    $joinBefore &&
    css`
      > [data-media-timeline-track] {
        /* 독립된 편집 행의 경계가 사선(subtle)에 묻히지 않도록 grid 윤곽 단계로 구분한다. */
        border-top-color: ${theme.color.border.default};
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    `}
  ${({ $joinAfter }) =>
    $joinAfter &&
    css`
      > [data-media-timeline-track] {
        border-bottom-width: 0;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    `}

  ${({ $toolsOpen, theme }) =>
    $toolsOpen &&
    css`
      > [data-media-timeline-track] {
        background-color: ${theme.color.bg.surfaceRaisedHigh};
        border-color: ${theme.color.border.strong};
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    `}
`;

export const TimelineNavigationFooter = styled.div`
  position: relative;
  height: 2rem;
  margin-top: 0.35rem;
`;

/* The heading sits at the top of the header gutter; lane captions share the column below it. */
export const TimelineTrackRowHeading = styled.div`
  position: absolute;
  inset-inline-end: calc(100% + 0.75rem);
  top: 0;
  width: calc(var(--c6-media-track-label-gutter) - 0.75rem);
  color: ${({ theme }) => theme.color.text.muted};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
`;

export const TimelineTrackRowLabel = styled(TruncatedText)`
  color: ${({ theme }) => theme.color.text.default};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
`;

export const TimelineTrackRowTitle = styled.div<{ $stacked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  > ${TimelineTrackRowLabel} {
    flex: 1;
    min-width: 0;
  }
  ${({ $stacked }) =>
    $stacked &&
    css`
      flex-direction: column;
      align-items: flex-start;
      > ${TimelineTrackRowLabel} {
        width: 100%;
      }
      /* The end edge, as in the inline layout: a row without the tools button keeps the
         consumer's buttons where the neighbouring rows have them. */
      > [data-media-timeline-row-actions] {
        align-self: flex-end;
      }
    `}
`;

export const TimelineTrackRowActions = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 0.125rem;
  margin-inline-start: auto;
`;

export const TimelineTrackHeaderSeparator = styled.div`
  position: absolute;
  inset-block: 0;
  inset-inline-start: -0.625rem;
  width: 0.5rem;
  cursor: col-resize;
  touch-action: none;
  border-radius: ${({ theme }) => theme.shape.radius.control};
  &::after {
    content: "";
    position: absolute;
    inset-block: 0;
    inset-inline-start: calc(50% - 0.5px);
    border-inline-start: 1px solid ${({ theme }) => theme.color.border.divider};
    ${controlTransition("border-color")}
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
  &:hover::after,
  &:focus-visible::after {
    border-color: ${({ theme }) => theme.color.border.strong};
  }
  &:focus-visible {
    ${controlFocusRing("-2px")}
  }
`;

/*
 * Lane captions use the whole header gutter and sit at the bottom-right corner of their band,
 * below the heading that owns the top of the first lane (title plus one controls line, ≈2.75rem
 * of a 4rem lane). The container is pointer-transparent so it never shadows heading controls;
 * only the caption text takes the pointer, for its truncation tooltip.
 */
export const TimelineTrackRowLanes = styled.div`
  position: absolute;
  inset-inline-end: calc(100% + 0.75rem);
  inset-block: 0;
  display: flex;
  flex-direction: column;
  width: calc(var(--c6-media-track-label-gutter) - 0.75rem);
  pointer-events: none;
  color: ${({ theme }) => theme.color.text.muted};
  font-size: ${({ theme }) => theme.typography.size.caption};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
`;
export const TimelineTrackRowLaneCaption = styled.div`
  display: flex;
  flex: 1 1 0;
  align-items: flex-end;
  justify-content: flex-end;
  min-height: 0;
  min-width: 0;
  padding-bottom: 0.3rem;
`;
/* The text truncates on its own block: as a flex container it would clip the caption's start. */
export const TimelineTrackRowLaneCaptionText = styled(TruncatedText)`
  max-width: 100%;
  line-height: 1rem;
  text-align: end;
  pointer-events: auto;
`;

export const TimelineTrackRowUnit = styled.span`
  color: ${({ theme }) => theme.color.text.muted};
  font-weight: ${({ theme }) => theme.typography.weight.regular};
`;

/** Metadata only: controls do not participate in the measured time lane or row height. */
export const TimelineTrackRowControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.125rem;
  min-width: 0;
`;

type SurfaceTrackType = "default" | "track";

/* A line needs vertical resolution that a seek/band row does not: the series row is taller, and
   its `rail` matches the full height of the other surface rows rather than their rail — below
   that a 2px stroke plus its inset has no room left to move. */
const TrackRowSurface = styled(TimelineTrack)<{
  $variant: MediaTimelineTrackVariant;
  $hasSeries: boolean;
  $laneCount: number;
}>`
  box-sizing: border-box;
  height: ${({ $variant, $hasSeries, $laneCount }) =>
    $hasSeries
      ? $variant === "rail"
        ? `calc(${$laneCount} * 2.2rem + 2px)`
        : `calc(${$laneCount} * 4rem + 2px)`
      : $variant === "rail"
        ? "calc(1.5rem + 2px)"
        : "calc(2.2rem + 2px)"};
`;

interface TrackRowProps extends Omit<ComponentPropsWithoutRef<"div">, "children"> {
  id: string;
  type: SurfaceTrackType;
  hasSeries?: boolean;
  /** Stacked plot bands; the row is this many series rows tall. */
  laneCount?: number;
  label?: string;
  variant?: MediaTimelineTrackVariant;
  disabled: boolean;
  dragTarget: TimelineDragTarget;
  focusVisible?: boolean;
  children: ReactNode;
}

export const SurfaceTimelineTrackRow = forwardRef<HTMLDivElement, TrackRowProps>(
  function SurfaceTimelineTrackRow(
    {
      id,
      type,
      hasSeries = false,
      laneCount,
      label,
      variant = "track",
      disabled,
      dragTarget,
      focusVisible = false,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <TrackRowSurface
        {...props}
        ref={ref}
        role={label ? "group" : undefined}
        aria-label={label}
        $variant={variant}
        $hasSeries={hasSeries}
        $laneCount={Math.max(1, laneCount ?? 1)}
        $disabled={disabled}
        $dragTarget={dragTarget}
        $focusVisible={focusVisible}
        data-media-timeline-track="true"
        data-media-timeline-track-id={id}
        data-media-timeline-track-type={type}
        data-media-timeline-track-variant={variant}
        data-media-timeline-track-lanes={
          laneCount !== undefined && laneCount > 1 ? laneCount : undefined
        }
      >
        {children}
      </TrackRowSurface>
    );
  },
);
const MarkerTrackRowSurface = styled(MarkerRail)<{ $variant: MediaTimelineTrackVariant }>`
  min-height: ${({ $variant }) => ($variant === "rail" ? "1.5rem" : "2.2rem")};
  margin-top: 0;
`;

interface MarkerTimelineTrackRowProps {
  id: string;
  label: string;
  variant?: MediaTimelineTrackVariant;
  children: ReactNode;
}

export function MarkerTimelineTrackRow({
  id,
  label,
  variant = "rail",
  children,
}: MarkerTimelineTrackRowProps) {
  return (
    <MarkerTrackRowSurface
      $variant={variant}
      data-media-timeline-track="true"
      data-media-timeline-track-id={id}
      data-media-timeline-track-type="markers"
      data-media-timeline-track-variant={variant}
    >
      <MarkerRailTrack role="group" aria-label={label} data-media-marker-rail="true">
        {children}
      </MarkerRailTrack>
    </MarkerTrackRowSurface>
  );
}
